/**
 * Patients List Page
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Skeleton,
  IconButton,
  Menu,
  MenuItem,
  Button,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  PersonAdd as PersonAddIcon,
  CheckCircle as CheckCircleIcon,
  Clear as ClearIcon,
  People as PeopleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterAltOff as ClearFiltersIcon,
} from '@mui/icons-material';
import { PageHeader, StatsCard, ConfirmDialog } from '../../components/common';
import {
  useGetPatientsQuery,
  useGetPatientsStatisticsQuery,
  useDeletePatientMutation,
} from '../../store/api/patientsApi';
import { Patient, LEAD_SOURCES, type LeadSource } from '../../store/api/patientsApi';
import { palette } from '../../theme/palette';
import moment from 'moment';
import toast from 'react-hot-toast';
import { usePermissions } from '../../hooks/usePermissions';
import { useTranslation } from 'react-i18next';
import { formatNumber as formatNumberLocalized } from '../../utils/format';

const emptyFilters = { is_active: '', gender: '', has_account: '', lead_source: '' };

const UsersListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canManagePatients = hasPermission('manage_patients');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(emptyFilters);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<Patient | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState<Patient | null>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(0); // Reset to first page when search changes
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Patients query (sync page size with backend)
  const { data: patientsData, isLoading: patientsLoading } = useGetPatientsQuery({
    page: page + 1,
    page_size: rowsPerPage,
    search: search || undefined,
    is_active: filters.is_active ? filters.is_active === 'true' : undefined,
    gender: (filters.gender as 'M' | 'F') || undefined,
    has_account: filters.has_account ? filters.has_account === 'true' : undefined,
    lead_source: (filters.lead_source as LeadSource) || undefined,
  });

  // Statistics query
  const { data: statsData } = useGetPatientsStatisticsQuery();

  // Mutations
  const [deletePatient, { isLoading: isDeletingPatient }] = useDeletePatientMutation();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, patient: Patient) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(patient);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  const handleDeletePatient = (patientId: number) => {
    const patient = patientsData?.results?.find((p: Patient) => p.id === patientId);
    if (patient) {
      setPatientToDelete(patient);
      setDeleteDialogOpen(true);
    }
  };

  const confirmDeletePatient = async () => {
    if (!patientToDelete) return;
    try {
      await deletePatient(patientToDelete.id).unwrap();
      toast.success(t('users.toast.deleteSuccess'));
      setDeleteDialogOpen(false);
      setPatientToDelete(null);
      handleMenuClose();
    } catch (err: any) {
      toast.error(err?.data?.message || t('users.toast.deleteError'));
    }
  };

  const formatNumber = (num: number): string => formatNumberLocalized(num);

  const patientsColumns = [
    { id: 'id', label: t('users.cols.id'), minWidth: 80 },
    { id: 'full_name', label: t('users.cols.name'), minWidth: 150 },
    { id: 'user_email', label: t('users.cols.userAccount'), minWidth: 150 },
    { id: 'age', label: t('users.cols.age'), minWidth: 80 },
    { id: 'gender', label: t('users.cols.gender'), minWidth: 100 },
    { id: 'phone_number', label: t('users.cols.phone'), minWidth: 150 },
    { id: 'orders_count', label: t('users.cols.orders'), minWidth: 80 },
    { id: 'email', label: t('users.cols.email'), minWidth: 200 },
    { id: 'is_active', label: t('users.cols.status'), minWidth: 100 },
    { id: 'created_at', label: t('users.cols.created'), minWidth: 120 },
  ];

  const stats = statsData?.data;

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={t('users.title')}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('nav.patients') },
        ]}
        action={
          canManagePatients ? (
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => navigate('/users/patients/create')}
            >
              {t('users.buttons.createPatient')}
            </Button>
          ) : undefined
        }
      />

      {/* Statistics Cards */}
      {stats && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(3, 1fr)',
            },
            gap: 2,
            mb: 3,
          }}
        >
          <StatsCard
            title={t('users.stats.totalPatients')}
            value={formatNumber(stats.total_patients)}
            icon={<PeopleIcon />}
            color={palette.brand[500]}
          />
          <StatsCard
            title={t('users.stats.activePatients')}
            value={formatNumber(stats.active_patients)}
            icon={<CheckCircleIcon />}
            color={palette.status.success}
          />
          <StatsCard
            title={t('users.stats.withUserAccount')}
            value={formatNumber(stats.patients_with_user_account)}
            icon={<PeopleIcon />}
            color={palette.status.info}
          />
        </Box>
      )}

      {/* Search Bar */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              fullWidth
              placeholder={t('users.filters.searchPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchInput && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchInput('');
                        setSearch('');
                      }}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr) auto' },
              gap: 2,
              alignItems: 'center',
              mt: 2,
            }}
          >
            {(
              [
                ['is_active', t('users.filters.status'), [['true', t('users.status.ACTIVE')], ['false', t('users.status.INACTIVE')]]],
                ['gender', t('users.filters.gender'), [['M', t('users.gender.M')], ['F', t('users.gender.F')]]],
                ['has_account', t('users.filters.account'), [['true', t('users.filters.withAccount')], ['false', t('users.filters.withoutAccount')]]],
                ['lead_source', t('users.filters.leadSource'), LEAD_SOURCES.map((src) => [src, t(`users.leadSources.${src}`)])],
              ] as [keyof typeof filters, string, string[][]][]
            ).map(([key, label, options]) => (
              <FormControl key={key} size="small" fullWidth>
                <InputLabel>{label}</InputLabel>
                <Select
                  label={label}
                  value={filters[key]}
                  onChange={(e) => {
                    setFilters((prev) => ({ ...prev, [key]: e.target.value }));
                    setPage(0);
                  }}
                >
                  <MenuItem value="">{t('users.filters.all')}</MenuItem>
                  {options.map(([value, text]) => (
                    <MenuItem key={value} value={value}>
                      {text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ))}
            <Button
              size="small"
              startIcon={<ClearFiltersIcon />}
              disabled={!Object.values(filters).some(Boolean)}
              onClick={() => {
                setFilters(emptyFilters);
                setPage(0);
              }}
            >
              {t('users.filters.clear')}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Patients Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {patientsColumns.map((column) => (
                  <TableCell key={column.id} sx={{ fontWeight: 600 }}>
                    {column.label}
                  </TableCell>
                ))}
                {canManagePatients && (
                  <TableCell sx={{ fontWeight: 600, width: 50 }}>{t('common.actions')}</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {patientsLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    {patientsColumns.map((column) => (
                      <TableCell key={column.id}>
                        <Skeleton variant="text" width="100%" />
                      </TableCell>
                    ))}
                    {canManagePatients && (
                      <TableCell>
                        <Skeleton variant="circular" width={40} height={40} />
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : patientsData?.results && patientsData.results.length > 0 ? (
                patientsData.results.map((patient: Patient) => (
                  <TableRow
                    key={patient.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/users/patients/${patient.id}`)}
                  >
                    <TableCell>{patient.id}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {patient.full_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {patient.user_email ? (
                        <Chip
                          label={patient.user_email}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t('users.noAccount')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{patient.age ?? t('users.na')}</TableCell>
                    <TableCell>
                      {patient.gender === 'M'
                        ? t('users.gender.M')
                        : patient.gender === 'F'
                          ? t('users.gender.F')
                          : t('users.na')}
                    </TableCell>
                    <TableCell>{patient.phone_number}</TableCell>
                    <TableCell>{patient.orders_count ?? 0}</TableCell>
                    <TableCell>{patient.email || t('users.na')}</TableCell>
                    <TableCell>
                      <Chip
                        label={patient.is_active ? t('users.status.ACTIVE') : t('users.status.INACTIVE')}
                        size="small"
                        color={patient.is_active ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {patient.created_at
                        ? moment(patient.created_at).format('MMM DD, YYYY')
                        : t('users.na')}
                    </TableCell>
                    {canManagePatients && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, patient)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={patientsColumns.length + (canManagePatients ? 1 : 0)}
                    align="center"
                    sx={{ py: 5 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {t('users.empty')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {patientsData && (
          <TablePagination
            component="div"
            count={patientsData.count || 0}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        )}
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedItem && (
          <>
            <MenuItem onClick={() => {
              navigate(`/users/patients/${selectedItem.id}/edit`);
              handleMenuClose();
            }}>
              <EditIcon sx={{ mr: 1 }} fontSize="small" />
              {t('common.edit')}
            </MenuItem>
            <MenuItem onClick={() => {
              handleDeletePatient(selectedItem.id);
              handleMenuClose();
            }}>
              <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
              {t('common.delete')}
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Delete Patient Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setPatientToDelete(null);
        }}
        onConfirm={confirmDeletePatient}
        title={t('users.dialog.deleteTitle')}
        message={t('users.dialog.deleteMessage', { name: patientToDelete?.full_name })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        loading={isDeletingPatient}
      />
    </Box>
  );
};

export default UsersListPage;
